import type { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@capturum/ui/api';
import { TranslateService } from '@ngx-translate/core';
import { NeqtoAuthService } from '@shared/services/neqto-auth.service';
import { catchError, throwError } from 'rxjs';

export const errorMessageInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);
  const authService = inject(NeqtoAuthService);
  const exceptionUrls = (errorResponse: HttpErrorResponse): boolean => {
    let response: boolean = false;

    response =
      ((errorResponse.status === 422 || errorResponse.status === 500) &&
        (errorResponse.url.endsWith('/auth/login') ||
          errorResponse.url.endsWith('/auth/recovery') ||
          errorResponse.url.includes('/chat/'))) ||
      // The flow lock heartbeat (FlowService.extendLockLeaseToken) already logs and handles every
      // status itself, on every tick — any status here would otherwise toast raw text repeatedly.
      errorResponse.url?.includes('/extend-lock-lease') ||
      errorResponse.url?.includes('/terminate-lease');

    return !response;
  };
  const defaultParsingErrors = (errorResponse: HttpErrorResponse): void => {
    if (exceptionUrls(errorResponse)) {
      if (errorResponse.error?.errors) {
        const errors: Dictionary<Record<string, string>> = errorResponse.error.errors;
        const errorValues = Object.values(errors);

        if (errorValues.length) {
          const message = errorValues
            .map((value) => {
              const messageValue = typeof value === 'string' ? value : Object.values(value).toString();

              return `<li>${messageValue}</li>`;
            })
            .filter(Boolean)
            .join('');

          toastService.error(translateService.instant('toast.error.title'), `<ul>${message}</ul>`);
        }
      } else if (errorResponse.error?.error?.message) {
        const message = errorResponse.error.error.message;

        toastService.error(translateService.instant('toast.error.title'), message);
      } else if ((typeof errorResponse.error === 'string' && errorResponse.error) || errorResponse.error?.error) {
        const message = typeof errorResponse.error === 'string' ? errorResponse.error : errorResponse.error.error;

        toastService.error(translateService.instant('toast.error.title'), message);
      }
    }
  };
  const catchErrorLogic = (errorResponse: HttpErrorResponse, request?: HttpRequest<any>): void => {
    switch (errorResponse.status) {
      case 401:
        // it should be handled in the notAuthorizedInterceptor
        void 0;
        break;
      default:
        defaultParsingErrors(errorResponse);
        break;
    }
  };
  const handleError = (errorResponse: HttpErrorResponse, request?: HttpRequest<unknown>): void => {
    if (
      errorResponse instanceof HttpErrorResponse &&
      errorResponse.error instanceof Blob &&
      errorResponse.headers.get('Content-Type') === 'application/json'
    ) {
      const reader = new FileReader();

      reader.addEventListener('loadend', (event: Event) => {
        const result = (event.target as FileReader).result;
        const errorFromBlob: { error: unknown } = { error: null };

        if (typeof result === 'string') {
          try {
            errorFromBlob.error = JSON.parse(result);
          } catch (e) {
            errorFromBlob.error = result;
          }
        } else {
          errorFromBlob.error = result;
        }

        catchErrorLogic(errorFromBlob as HttpErrorResponse, request);
      });
      reader.readAsText(errorResponse.error);
    }
  };

  return next(req).pipe(
    catchError((errorResponse: HttpErrorResponse) => {
      handleError(errorResponse, req);
      catchErrorLogic(errorResponse, req);

      return throwError(() => {
        return errorResponse;
      });
    }),
  );
};
